import { apiCache } from "./cache"

export const getApiUrl = (): string => {
  const raw = process.env.NEXT_PUBLIC_API_URL || "https://api.syncmenu.ao"
  if (/^https?:\/\//i.test(raw)) return raw
  return `https://${raw}`
}

const API_URL = getApiUrl()

// Tipos para a API
export interface Dish {
  _id: string
  nome: string
  descricao: string
  preco: number
  precoPromocional?: number
  emPromocao?: boolean
  ingredientes: string[]
  tempo_preparo: number
  destacado: boolean
  disponibilidade: boolean
  urls: string[]
  estabelecimentoId: string
  funcionarioId: string
  categoriamenuId: {
    _id: string
    nome: string
  }
  tipo: string,
  seccaoId: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  __v: number
}

export interface Category {
  nome: string
  descricao: string
  urls: string[]
  pratos: Dish[]
}

export interface OrderItem {
  prato: string
  quantidade: number
}

export interface OrderBuffete {
  buffeteId: string
  quantidade: number
}

export interface Order {
  nome_cliente: string
  telefone: string
  itens: OrderItem[]
  /** Bufetes requisitados pelo cliente (anúncio do menu) */
  buffetes?: OrderBuffete[]
  paymentMethod: string,
  /** Sugestões/observações escritas pelo cliente no carrinho */
  observacoes?: string
  /** Cliente autenticado por telefone (opcional) */
  clienteId?: string
  localAtendimentoId: string
  token?: string
}

export interface OrderStatus {
  _id: string
  id?: string
  // A API devolve variantes ("Em preparo"/"Preparando"/"preparando", "Entregue"/"finalizado").
  // Normalizar sempre com normalizeOrderStatus() de "@/lib/order-status".
  status: string
  nome_cliente: string
  telefone?: string
  itens: Array<{
    prato: {
      _id: string
      nome: string
      preco: number
      urls: string[]
    }
    quantidade: number
    preco_unitario: number
    subtotal: number
  }>
  total: number
  createdAt: string
  updatedAt: string
}

// Função para pré-carregar imagens
export function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = url
  })
}

// Retorna o preço efetivo (usa o preço promocional se estiver em promoção)
export function effectivePrice(dish: Pick<Dish, "preco" | "precoPromocional" | "emPromocao">): number {
  return dish.emPromocao && dish.precoPromocional != null ? dish.precoPromocional : dish.preco
}

// Função para buscar o menu com cache
export async function fetchMenu(localId: string, menuId?: string | null): Promise<Category[]> {
  const cacheKey = `menu_${localId}_${menuId || "default"}`

  // Verificar se existe no cache
  const cachedData = apiCache.get<Category[]>(cacheKey)
  if (cachedData) {
    console.log("Usando dados do cache para o menu")
    return cachedData
  }

  try {
    console.log("Buscando menu da API...")
    const url = menuId
      ? `${API_URL}/generic/menu/${localId}?tipo=Pratos&menuId=${menuId}`
      : `${API_URL}/generic/menu/${localId}?tipo=Pratos`
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Erro ao buscar menu: ${response.status}`)
    }

    const data: Category[] = await response.json()

    // Armazenar no cache (expira em 2 minutos)
    apiCache.set(cacheKey, data, 2 * 60 * 1000)

    // Pré-carregar as primeiras imagens de cada categoria em segundo plano
    setTimeout(() => {
      data.forEach((category) => {
        if (category.urls && category.urls.length > 0) {
          preloadImage(category.urls[0]).catch(() => { })
        }

        // Pré-carregar a primeira imagem de cada prato destacado
        category.pratos
          .filter((dish) => dish.destacado)
          .slice(0, 3)
          .forEach((dish) => {
            if (dish.urls && dish.urls.length > 0) {
              preloadImage(dish.urls[0]).catch(() => { })
            }
          })
      })
    }, 0)



    return data
  } catch (error) {
    console.error("Erro ao buscar menu:", error)
    throw error
  }
}
export async function fetchMenuBebidas(localId: string, menuId?: string | null): Promise<Category[]> {
  const cacheKey = `menu_Bebidas_${localId}_${menuId || "default"}`

  // Verificar se existe no cache
  const cachedData = apiCache.get<Category[]>(cacheKey)
  if (cachedData) {
    console.log("Usando dados do cache para o menu")
    return cachedData
  }

  try {
    const url = menuId
      ? `${API_URL}/generic/menu/${localId}?tipo=Bebidas&menuId=${menuId}`
      : `${API_URL}/generic/menu/${localId}?tipo=Bebidas`
    const response = await fetch(url)
    console.log("bebidas", response)
    if (!response.ok) {
      throw new Error(`Erro ao buscar menu: ${response.status}`)
    }

    const data: Category[] = await response.json()

    // Armazenar no cache (expira em 2 minutos)
    apiCache.set(cacheKey, data, 2 * 60 * 1000)

    // Pré-carregar as primeiras imagens de cada categoria em segundo plano
    setTimeout(() => {
      data.forEach((category) => {
        if (category.urls && category.urls.length > 0) {
          preloadImage(category.urls[0]).catch(() => { })
        }

        // Pré-carregar a primeira imagem de cada prato destacado
        category.pratos
          .filter((dish) => dish.destacado)
          .slice(0, 3)
          .forEach((dish) => {
            if (dish.urls && dish.urls.length > 0) {
              preloadImage(dish.urls[0]).catch(() => { })
            }
          })
      })
    }, 0)

    return data
  } catch (error) {
    console.error("Erro ao buscar menu:", error)
    throw error
  }
}
// Função para enviar pedido com token e telefone
export async function submitOrder(order: Order): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/generic/pedidos`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(order.token && { Authorization: `Bearer ${order.token}` }),
      },
      body: JSON.stringify(order),
    })
    console.log(order);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      // Verificar se é erro de token expirado
      if (response.status === 401 || response.status === 403) {
        throw new Error("Sessão expirada. Por favor, escaneie o QR code novamente.")
      }

      throw new Error(errorData.message || `Erro ao enviar pedido: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error("Erro ao enviar pedido:", error)
    throw error
  }
}

// Função para verificar o status do pedido por ID
export async function checkOrderStatus(orderId: string): Promise<OrderStatus> {
  const cacheKey = `order_status_${orderId}`

  try {
    console.log(`[API] Buscando status do pedido ${orderId}...`)

    const response = await fetch(`${API_URL}/generic/pedidos/${orderId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      // Se o pedido não for encontrado
      if (response.status === 404) {
        throw new Error("Pedido não encontrado")
      }

      throw new Error(`Erro ao verificar status do pedido: ${response.status}`)
    }

    const orderData = await response.json()
    console.log(`[API] Status do pedido ${orderId} obtido:`, orderData)

    // Cache por 30 segundos para evitar muitas requisições
    apiCache.set(cacheKey, orderData, 30 * 1000)

    return orderData
  } catch (error) {
    console.error(`[API] Erro ao verificar status do pedido ${orderId}:`, error)

    // Tentar buscar do cache em caso de erro de rede
    const cachedData = apiCache.get<OrderStatus>(cacheKey)
    if (cachedData) {
      console.log(`[API] Usando dados do cache para pedido ${orderId}`)
      return cachedData
    }

    throw error
  }
}

// Função para buscar múltiplos pedidos (se necessário no futuro)
export async function fetchOrdersByCustomer(customerPhone: string): Promise<OrderStatus[]> {
  try {
    console.log(`[API] Buscando pedidos do cliente ${customerPhone}...`)

    const response = await fetch(
      `${API_URL}/generic/pedidos?telefone=${encodeURIComponent(customerPhone)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Erro ao buscar pedidos do cliente: ${response.status}`)
    }

    const data = await response.json()
    const orders: OrderStatus[] = Array.isArray(data) ? data : data.pedidos ?? []
    console.log(`[API] Pedidos do cliente ${customerPhone} obtidos:`, orders.length)

    return orders
  } catch (error) {
    console.error(`[API] Erro ao buscar pedidos do cliente ${customerPhone}:`, error)
    throw error
  }
}

// Interface para dados do estabelecimento
export interface Estabelecimento {
  _id: string
  id?: string
  nome: string
  domain?: string
  template?: "default" | "modern" | "elegant" | "vibrant" | "minimal"
  descricao?: string
  telefone?: string
  endereco?: string
  logo?: string
  urls?: string[]
  horariosAtendimento?: {
    [key: string]: {
      abertura: string
      fechamento: string
    }
  }
  [key: string]: any
}

// Função para buscar dados do estabelecimento
export async function fetchEstabelecimento(localId: string, menuId?: string | null): Promise<Estabelecimento | null> {
  const cacheKey = `estabelecimento_${localId}_${menuId || "default"}`

  // Verificar se existe no cache
  const cachedData = apiCache.get<Estabelecimento>(cacheKey)
  if (cachedData) {
    console.log("[API] Usando dados do estabelecimento do cache")
    return cachedData
  }

  try {
    console.log(`[API] Buscando dados do estabelecimento ${localId}...`)

    // Tentar múltiplas endpoints possíveis
    const genericUrl = menuId
      ? `${API_URL}/generic/estabelecimento/${localId}?menuId=${menuId}`
      : `${API_URL}/generic/estabelecimento/${localId}`
    const endpoints = [
      `${API_URL}/estabelecimentos/${localId}`,
      `${API_URL}/local-atendimento/${localId}`,
      genericUrl,
    ]

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })

        if (response.ok) {
          const data = await response.json()
          console.log("[API] Dados do estabelecimento obtidos:", data)

          // O endpoint genérico devolve { localDeAtendimento, menu }. Expor o tema do menu
          // como "template" para o ThemeRenderer usar o tema definido no menu.
          if (data?.menu?.theme) {
            data.template = data.menu.theme
          }

          // Cachear os dados
          apiCache.set(cacheKey, data, 60 * 60 * 1000) // Cache por 1 hora
          
          return data
        }
      } catch (error) {
        console.log(`[API] Endpoint ${endpoint} falhou, tentando próximo...`)
        continue
      }
    }

    console.warn(`[API] Nenhum endpoint funcionou para buscar estabelecimento ${localId}`)
    return null
  } catch (error) {
    console.error(`[API] Erro ao buscar dados do estabelecimento ${localId}:`, error)
    return null
  }
}

// Função para gerar um token único (mantida para compatibilidade)
export function generateToken(): string {
  return (
    Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
  )
}

// ===== Bufete (anúncio do menu) =====
export interface BuffetePratoPopulado {
  pratoId: { _id: string; nome?: string; urls?: string[] } | string
  quantidade: number
}

export interface Buffete {
  _id: string
  nome: string
  descricao?: string
  urls: string[]
  preco: number
  quantidadePratos: number
  /** Pratos que compõem o bufete (populados pelo endpoint público) */
  pratos?: BuffetePratoPopulado[]
  dataInicio?: string | null
  dataFim?: string | null
}

/** Capa do bufete: imagem própria ou, na falta dela, a foto do primeiro prato. */
export function capaDoBufete(b: Buffete): string | null {
  const propria = b.urls?.find((u) => u && !u.includes("placeholder"))
  if (propria) return propria
  for (const p of b.pratos || []) {
    const urls = typeof p.pratoId === "object" ? p.pratoId?.urls : undefined
    const url = urls?.find((u) => u && !u.includes("placeholder"))
    if (url) return url
  }
  return null
}

/** Lista de pratos do bufete já resolvida (nome + urls), se o backend os enviou. */
export function pratosDoBufete(b: Buffete): Array<{ nome: string; urls: string[]; quantidade: number }> {
  return (b.pratos || [])
    .map((p) => ({
      nome: typeof p.pratoId === "object" ? p.pratoId?.nome || "Prato" : "Prato",
      urls: typeof p.pratoId === "object" ? p.pratoId?.urls || [] : [],
      quantidade: p.quantidade || 1,
    }))
}

/** Busca o bufete anunciado para o local (prioriza o bufete do menu ativo). */
/** Verifica se o bufete está dentro do período de validade (quando tem datas).
 * Aceita "YYYY-MM-DD" ou ISO completo. */
export function bufeteDentroDaValidade(b: Buffete, agora = new Date()): boolean {
  const parse = (v?: string | null): Date | null => {
    if (!v) return null
    const d = new Date(v.length === 10 ? `${v}T12:00:00` : v)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const inicio = parse(b.dataInicio)
  const fim = parse(b.dataFim)
  if (inicio && inicio > agora) return false
  if (fim && fim < agora) return false
  return true
}

export async function fetchBuffete(localId: string, menuId?: string | null): Promise<Buffete | null> {
  if (!localId) return null

  const cacheKey = `buffete_${localId}_${menuId || "default"}`
  const cached = apiCache.get<Buffete[]>(cacheKey)
  if (cached) return cached[0] || null

  try {
    const url = menuId
      ? `${API_URL}/generic/buffete/${localId}?menuId=${menuId}`
      : `${API_URL}/generic/buffete/${localId}`
    const response = await fetch(url)
    if (!response.ok) return null

    const data: Buffete[] = await response.json()
    // Apenas bufetes temporariamente válidos (respeita início/fim definidos no painel)
    const lista = (Array.isArray(data) ? data : []).filter((b) => bufeteDentroDaValidade(b))
    apiCache.set(cacheKey, lista, 2 * 60 * 1000)

    // Pré-carregar a imagem do anúncio em segundo plano
    if (lista[0]?.urls?.[0]) {
      preloadImage(lista[0].urls[0]).catch(() => {})
    }

    return lista[0] || null
  } catch (error) {
    console.error("Erro ao buscar bufete:", error)
    return null
  }
}

// ===== Sessão do cliente por telefone (login automático) =====
export interface ClienteSessao {
  _id: string
  nome: string
  telefone: string
}

/** Encontra ou cria o cliente pelo número de telefone. */
export async function iniciarSessaoCliente(telefone: string, nome?: string): Promise<ClienteSessao | null> {
  try {
    const response = await fetch(`${API_URL}/generic/clientes/sessao`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ telefone, nome }),
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error("Erro ao iniciar sessão do cliente:", error)
    return null
  }
}

// ===== Feedback do cliente (após entrega do pedido) =====
export interface FeedbackInput {
  pedidoId: string
  rating: number
  comentario?: string
  nome?: string
  telefone?: string
}

/** Envia a avaliação do pedido (1–5 estrelas + comentário). */
export async function enviarFeedback(input: FeedbackInput): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_URL}/generic/feedbacks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { ok: false, message: data?.message || "Não foi possível enviar o feedback." }
    }
    return { ok: true, message: data?.message || "Obrigado pelo seu feedback!" }
  } catch (error) {
    console.error("Erro ao enviar feedback:", error)
    return { ok: false, message: "Sem ligação ao servidor. Tenta novamente." }
  }
}

/** Verifica localmente se este pedido já foi avaliado neste dispositivo. */
export function feedbackJaEnviado(pedidoId: string): boolean {
  try {
    return localStorage.getItem(`feedback_enviado_${pedidoId}`) === "1"
  } catch {
    return false
  }
}

export function marcarFeedbackEnviado(pedidoId: string) {
  try {
    localStorage.setItem(`feedback_enviado_${pedidoId}`, "1")
  } catch {}
}

// ===== Perfil do cliente (aba Conta) =====
export interface DadosFiscais {
  tipo: "pessoal" | "empresa"
  nif?: string
  nomeEmpresa?: string
  endereco?: string
}

export interface FaturaClienteItem {
  _id: string
  numero: number
  codigo: string
  total: number
  estado: "emitida" | "validada_agt" | "anulada"
  agt?: { validadaEm: string | null; codigoValidacao: string }
  createdAt: string
  paymentMethod?: string
  pedidoId?: string
  nomeCliente?: string
  faturacao?: {
    tipo: "pessoal" | "empresa"
    nif?: string
    nomeEmpresa?: string
    endereco?: string
  }
  itens?: Array<{
    nome?: string
    quantidade?: number
    precoUnitario?: number
    subtotal?: number
  }>
  estabelecimentoId?: {
    _id?: string
    nome?: string
    urls?: string[]
    telefone?: string
    endereco?: string
    nif?: string
  } | null
}

export interface PerfilCliente {
  nome: string
  telefone: string
  dadosFiscais: DadosFiscais
  totalEstabelecimentos: number
  estabelecimentos: Array<{
    estabelecimentoId: string
    nome: string
    logo: string | null
    visitas: number
    ultimaVisita: string | null
  }>
  faturas: FaturaClienteItem[]
}

/** Estabelecimentos que já visitou + faturas, a partir do telefone. */
export async function obterPerfilCliente(telefone: string): Promise<PerfilCliente | null> {
  try {
    const response = await fetch(
      `${API_URL}/generic/clientes/${encodeURIComponent(telefone.trim())}/perfil`,
    )
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.error("Erro ao carregar perfil do cliente:", error)
    return null
  }
}

/** Guarda os dados de faturação (pessoal ou empresa com NIF). */
export async function guardarDadosFiscais(
  telefone: string,
  dados: DadosFiscais,
): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_URL}/generic/clientes/dados-fiscais`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ telefone: telefone.trim(), dadosFiscais: dados }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) return { ok: false, message: data?.message || "Não foi possível guardar." }
    return { ok: true, message: data?.message }
  } catch (error) {
    console.error("Erro ao guardar dados fiscais:", error)
    return { ok: false, message: "Sem ligação ao servidor." }
  }
}

// ===== Cancelamento pelo cliente (menu QR) =====
export const MOTIVOS_CANCELAMENTO_CLIENTE = [
  "Demora no atendimento",
  "Pedido errado",
  "Desisti do pedido",
  "Vou pedir outra coisa",
] as const

/** Cliente cancela um pedido ainda Pendente, com motivo obrigatório. */
export async function cancelarPedidoCliente(
  pedidoId: string,
  motivo: string,
  outro?: string,
): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_URL}/generic/pedidos/${pedidoId}/cancelar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ motivo, outro }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { ok: false, message: data?.message || "Não foi possível cancelar." }
    }
    return { ok: true, message: data?.message || "Pedido cancelado." }
  } catch (error) {
    console.error("Erro ao cancelar pedido:", error)
    return { ok: false, message: "Sem ligação ao servidor. Tenta novamente." }
  }
}